import qs from 'qs';

import { API } from '~apis/axios/instance';
import { CardProjectionType } from '~apis/card';
import { FxRuleType } from '~components/ui/currencyexchange/FxRuleTypeStore';
import { ResponseBaseWithData } from '~types/response';

export type AnalysisListItem = {
  id: number;
  name: string;
};

export type RangeType = 'DATE' | 'WEEK' | 'MONTH' | 'QUARTER' | 'YEAR' | 'TOTAL';
export type DateRangeUnit = 'DAY' | 'WEEK' | 'MONTH' | 'QUARTER' | 'YEAR';
export enum DateRangePreset {
  TODAY = 'TODAY',
  YESTERDAY = 'YESTERDAY',
  LAST_7_DAYS = 'LAST_7_DAYS',
  LAST_30_DAYS = 'LAST_30_DAYS',
  LAST_60_DAYS = 'LAST_60_DAYS',
  LAST_90_DAYS = 'LAST_90_DAYS',
  THIS_WEEK = 'THIS_WEEK',
  LAST_WEEK = 'LAST_WEEK',
  LAST_2_WEEKS = 'LAST_2_WEEKS',
  LAST_4_WEEKS = 'LAST_4_WEEKS',
  LAST_12_WEEKS = 'LAST_12_WEEKS',
  THIS_MONTH = 'THIS_MONTH',
  LAST_MONTH = 'LAST_MONTH',
  LAST_3_MONTHS = 'LAST_3_MONTHS',
  LAST_6_MONTHS = 'LAST_6_MONTHS',
  THIS_QUARTER = 'THIS_QUARTER',
  LAST_QUARTER = 'LAST_QUARTER',
  LAST_2_QUARTERS = 'LAST_2_QUARTERS',
  THIS_YEAR = 'THIS_YEAR',
  LAST_YEAR = 'LAST_YEAR',
  LAST_2_YEARS = 'LAST_2_YEARS',
}

export interface AnalysisResponse {
  table: AnalysisTableData;
}

export type RowGroupDisplayType = 'SINGLE_COLUMN' | 'MULTIPLE_COLUMN';
export type COLUMN_SET = 'CATEGORY' | 'GROUP' | 'BASE_DATE' | 'BUSINESS_ENTITY' | 'AMOUNT';
export type AmountDisplayUnit = 'WON' | 'MILLION_WON';
export interface AnalysisTableData {
  id?: number;
  name: string;
  columns: IColumnsItem[];
  filters: IFiltersItem[];
  startDate?: string;
  endDate?: string;
  dateRangeUnit: DateRangeUnit;
  dateRangePreset: DateRangePreset;
  excludeEmptyValue: boolean;
  dateUnit: RangeType;
  fxRuleType: FxRuleType;
  rowGroupDisplayType: RowGroupDisplayType;
  companyId: string;
  amountDisplayUnit: AmountDisplayUnit;
}
export interface IColumnsItem {
  id?: number;
  columnSet: COLUMN_SET;
  columnSetIdx: number;
  tableId?: number;
  name: string;
  pivot: boolean;
  isActive: boolean;
  pivotIndex: number;
  rowGroup: boolean;
  rowGroupIndex: number;
}

export type ColumnKey = 'CATEGORY' | 'GROUP' | 'BASE_DATE' | 'BUSINESS_ENTITY' | 'AMOUNT';
export interface IFiltersItem {
  id?: number;
  tableId?: number;
  columnSet: COLUMN_SET;
  value: string;
}

export const createAnalysis = ({ companyId }: { companyId: string }) => {
  return API.post<ResponseBaseWithData<AnalysisResponse>>(
    `/api/v3/companies/${companyId}/analysis`,
  );
};

export const updateAnalysis = ({
  companyId,
  tableId,
  data,
}: {
  companyId: string;
  tableId: number;
  data: AnalysisTableData;
}) => {
  return API.put(`/api/v3/companies/${companyId}/analysis/${tableId}`, {
    data,
  });
};

export const deleteAnalysis = ({ companyId, tableId }: { companyId: string; tableId: number }) => {
  return API.delete<ResponseBaseWithData>(`/api/v3/companies/${companyId}/analysis/${tableId}`);
};

export const duplicateAnalysis = ({
  companyId,
  tableId,
}: {
  companyId: string;
  tableId: number;
}) => {
  return API.post<ResponseBaseWithData<AnalysisResponse>>(
    `/api/v3/companies/${companyId}/analysis/${tableId}/duplicate`,
  );
};

export const renameAnalysis = ({
  companyId,
  tableId,
  name,
}: {
  companyId: string;
  tableId: number;
  name: string;
}) => {
  return API.patch(`/api/v3/companies/${companyId}/analysis/${tableId}/name`, {
    data: {
      name,
    },
  });
};

export const getAnalysisList = ({ companyId }: { companyId: string }) => {
  return API.get<ResponseBaseWithData<{ items: AnalysisListItem[] }>>(
    `/api/v3/companies/${companyId}/analysis`,
  );
};

export type AnalysisRequestParams = {
  startDate: string;
  endDate: string;
  rangeType: RangeType;
  fxRuleType?: FxRuleType;
  excludeEmptyItem?: boolean;
};

export const getAnalysisById = ({ companyId, id }: { companyId: string; id: number }) => {
  return API.get<ResponseBaseWithData<AnalysisResponse>>(
    `/api/v3/companies/${companyId}/analysis/${id}`,
  );
};

export interface AnalysisItemsResponse {
  items: AnalysisItem[];
}
export interface AnalysisItem {
  baseDate: string;
  categoryLabelKey: string;
  groupLabelId: number;
  businessEntityId: number;
  categoryHierarchies: string[];
  categoryTreeKey: string;
  contraCategoryTreeKey: string;
  groupHierarchies: number[];
  businessEntityName: string;
  isUnclassified: boolean;
  isCardPayment: boolean;

  orderIndex: number;
  value: IValue;
  type: string;
}
interface IValue {
  value: number;
  type: string;
}

type FilterType = 'VALUE' | 'FUNCTION';

export type AnalysisItemRequest = {
  companyId: string;
  startDate: string;
  endDate: string;
  rangeType: RangeType;
  fxRuleType?: FxRuleType;
  cardProjectionType?: CardProjectionType;
  excludeEmptyItem?: boolean;
};

export const getAnalysisItems = ({
  companyId,
  startDate,
  endDate,
  rangeType,
  fxRuleType,
  excludeEmptyItem,
  cardProjectionType = 'BILLING',
}: AnalysisItemRequest) => {
  const queryParam = qs.stringify({
    startDate,
    endDate,
    rangeType,
    fxRuleType,
    excludeEmptyItem,
    cardProjectionType,
  });
  return API.get<ResponseBaseWithData<AnalysisItemsResponse>>(
    `/api/v3/companies/${companyId}/analysis/items?${queryParam}`,
  );
};

export type BusinessEntityFilterItem = {
  name: string;
  columnSet: 'BUSINESS_ENTITY';
  type: FilterType;
  key: string;
  description: string;
  checked: boolean;
};

export type TreeFilterItem = {
  checked: boolean;
  columnSet: ColumnKey;
  description: null | string;
  key: null | string;
  name: string;
  type: FilterType;
};
export type FilterTreeItem = {
  key: string;
  children: FilterTreeItem[];
  value: TreeFilterItem;
};

export interface FilterSetResponse {
  categoryFilterTree: FilterTreeItem;
  groupFilterTree: FilterTreeItem;
  businessEntityList: BusinessEntityFilterItem[];
}

export const getFilterSetForAnalysis = ({
  companyId,
  tableId,
}: {
  companyId: string;
  tableId: number;
}) => {
  return API.get<ResponseBaseWithData<FilterSetResponse>>(
    `/api/v3/companies/${companyId}/analysis/${tableId}/filter-set`,
  );
};
